import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Button,
  message,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getAssetsList, getClientid } from "redux/selectors";
import { setAssetsList } from "redux/actions";

const { Option } = Select;
const { TextArea } = Input;

const ModalWrap = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
  }
  .ant-modal-body {
    padding: 0px 24px 24px 24px;
  }
  .custom-upload .ant-upload {
    width: 100%;
  }
  .upload-box {
    width: 100%;
    height: 42px;
    border-radius: 12px;
    background: #cfd6ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #cfd6ff;
  }
  .upload-box:hover {
    background: #bcc6ff;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

/* ── Existing attachment display ── */
const ExistingFile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8faff;
`;

const FileName = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #2563eb;
  font-weight: 500;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #ef4444;
  font-size: 15px;
  display: flex;
  align-items: center;
  padding: 0 2px;
  &:hover {
    color: #b91c1c;
  }
`;

/* ─── Component ──────────────────────────────────────────── */
const AddAssetModal = ({
  open,
  onClose,
  categoryList = [],
  preselectedCategory,
  editData,
  onSuccess,
}) => {
  console.log("editdata,editData", editData);
  const isEdit = !!editData;
  const assetsList = useSelector(getAssetsList);
  const clientId = useSelector(getClientid);
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState("");

  /* Track existing attachment separately from new upload */
  const [existingFile, setExistingFile] = useState(null); // { name, key } from editData
  const [existingRemoved, setExistingRemoved] = useState(false); // user clicked ✕

  const selectedCategory = Form.useWatch("category", form);
  const selectedCategoryObj = categoryList.find(
    (c) => c.categoryname === selectedCategory
  );
  const autoFilledType = selectedCategoryObj?.categorytype ?? "";
  const modelOptions = selectedCategoryObj?.model ?? [];
  const autoMasterid = selectedCategoryObj?.masterid ?? "";

  /* ── Pre-fill on open ── */
  useEffect(() => {
    if (open) {
      if (isEdit && editData) {
        form.setFieldsValue({
          assetName: editData.assetname,
          category: editData.categoryname,
          categoryType: editData.categorytype,
          model: editData.model,
          purchasedOn: editData.purchasedon
            ? dayjs(editData.purchasedon)
            : null,
          warrantyEnds: editData.warrantyends
            ? dayjs(editData.warrantyends)
            : null,
          description: editData.description,
        });
        setDescription(editData.description || "");

        // Populate existing attachment if present
        if (editData.filename) {
          setExistingFile({
            name: editData.filename,
            key: editData.filekey || "",
          });
          setExistingRemoved(false);
        } else {
          setExistingFile(null);
          setExistingRemoved(false);
        }
      } else if (preselectedCategory) {
        form.setFieldValue("category", preselectedCategory.categoryname);
        setExistingFile(null);
        setExistingRemoved(false);
      }
    }
  }, [open, editData, preselectedCategory]);

  /* ── Auto-fill Category Type on category change ── */
  useEffect(() => {
    if (selectedCategoryObj) {
      form.setFieldValue("categoryType", autoFilledType);
      if (!isEdit) form.setFieldValue("model", undefined);
    }
  }, [selectedCategory]);

  /* ── File upload helper ── */
  const handleDocumentSubmit = async (file) => {
    try {
      const res = await CALL_API(`assets/assets-file`, "POST", {
        filename: file.name,
        type: file.type,
      });
      if (res?.code !== STATUS_CODE.SUCCESS)
        throw new Error("Failed to get upload URL");
      await fetch(res.url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      return res.key;
    } catch {
      message.error("File upload failed");
      return "";
    }
  };

  const fetchMasterData = async () => {
    const { code, assetsCategories } = await CALL_API(
      `assets/getassets/all`,
      "get"
    );
    if (code === STATUS_CODE.SUCCESS) dispatch(setAssetsList(assetsCategories));
  };

  /* ── Submit ── */
  const handleFinish = async (values) => {
    try {
      setLoading(true);

      const fileObj = values?.attachment?.[0]?.originFileObj || null;
      let uploadedKey = editData?.objectkey || "";
      let fileName = editData?.fileName || "";

      if (fileObj) {
        // New file selected — upload it
        const videoTypes = [
          "video/mp4",
          "video/mpeg",
          "video/quicktime",
          "video/x-msvideo",
          "video/x-ms-wmv",
          "video/webm",
          "video/ogg",
        ];
        if (
          videoTypes.includes(fileObj.type) ||
          fileObj.type?.startsWith("video/")
        ) {
          message.error("Video files are not allowed");
          return;
        }
        uploadedKey = await handleDocumentSubmit(fileObj);
        fileName = fileObj.name;
        if (!uploadedKey) return;
      } else if (existingFile && !existingRemoved) {
        // Existing file kept — retain its key and name
        uploadedKey = existingFile.key;
        fileName = existingFile.name;
      }
      // If existingRemoved === true and no new file → fileName & uploadedKey stay "" (cleared)

      const payload = {
        assetname: values.assetName,
        categoryname: values.category,
        categorytype: values.categoryType,
        model: values.model,
        purchasedon: values.purchasedOn,
        warrantyends: values.warrantyEnds,
        description: values.description,
        categoryid: preselectedCategory?.categoryid,
        filename: fileName,
        filekey: uploadedKey,
        masterid: autoMasterid,
      };

      const { code } = isEdit
        ? await CALL_API(
            `assets/update-asset/${editData.assetid}`,
            "patch",
            payload
          )
        : await CALL_API("/assets/addassets", "post", payload);

      if (code === STATUS_CODE.SUCCESS) {
        await fetchMasterData();
        message.success(
          isEdit ? "Asset updated successfully" : "Asset added successfully"
        );
        onSuccess?.();
        handleClose();
      } else {
        message.error(
          isEdit ? "Failed to update asset" : "Failed to add asset"
        );
      }
    } catch (error) {
      console.error("AddAssetModal error:", error);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setDescription("");
    setExistingFile(null);
    setExistingRemoved(false);
    onClose();
  };

  /* ── Whether to show the upload button ── */
  // Show Upload when: no existing file, OR existing file was removed AND no new file selected yet
  const newFileSelected = !!form.getFieldValue("attachment")?.length;
  const showUploadBtn = !existingFile || existingRemoved;
  const showExistingFile = existingFile && !existingRemoved;

  return (
    <ModalWrap
      title={
        isEdit
          ? `Editing Asset – ${editData?.assetname || ""}`
          : `Adding Asset${selectedCategory ? ` – ${selectedCategory}` : ""}`
      }
      open={open}
      onCancel={handleClose}
      centered
      width={500}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 16 }}
        onFinish={handleFinish}
      >
        {/* Asset Name */}
        <Form.Item
          label="ID or Name of the Asset"
          name="assetName"
          rules={[
            { required: true, message: "Asset ID or name is required" },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();

                const normalised = value.trim().toLowerCase();

                const duplicate = assetsList?.some((asset) => {
                  const isSameName =
                    asset.assetname?.trim().toLowerCase() === normalised;

                  if (!isSameName) return false;

                  // ── Edit mode: ignore match if it belongs to the current record ──
                  if (isEdit && editData) {
                    return asset.assetname !== editData.assetname;
                  }

                  return true; // Add mode — any match is a duplicate
                });

                return duplicate
                  ? Promise.reject(
                      new Error("This Asset ID/Name already exists")
                    )
                  : Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="e.g. LID1234" />
        </Form.Item>

        {/* Category — disabled always */}
        <Form.Item
          label="Category"
          name="category"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select
            placeholder="Select category"
            disabled
            showSearch
            optionFilterProp="children"
          >
            {categoryList.map((c) => (
              <Option
                key={c.categoryid ?? c.categoryname}
                value={c.categoryname}
              >
                {c.categoryname}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Category Type + Model */}
        <TwoCol>
          <Form.Item label="Category Type" name="categoryType">
            <Select placeholder="Auto-filled" disabled value={autoFilledType}>
              {autoFilledType && (
                <Option value={autoFilledType}>{autoFilledType}</Option>
              )}
            </Select>
          </Form.Item>
          <Form.Item
            label="Model"
            name="model"
            rules={[{ required: true, message: "Please select a model" }]}
          >
            <Select
              placeholder="Select model"
              disabled={!selectedCategory}
              maxTagCount="responsive"
            >
              {modelOptions.map((m) => (
                <Option key={m} value={m}>
                  {m}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </TwoCol>

        {/* Dates */}
        <TwoCol>
          <Form.Item label="Purchased on (Optional)" name="purchasedOn">
            <DatePicker
              className="w-100"
              format="DD MMM, YYYY"
              placeholder="Select date"
            />
          </Form.Item>
          <Form.Item label="Warranty Ends (Optional)" name="warrantyEnds">
            <DatePicker
              className="w-100"
              format="DD MMM, YYYY"
              placeholder="Select date"
            />
          </Form.Item>
        </TwoCol>

        {/* Description */}
        <Form.Item
          label="Description"
          name="description"
          rules={[
            { required: true, message: "Description is required" },
            { max: 250, message: "Max 250 chars" },
          ]}
        >
          <TextArea
            rows={4}
            maxLength={250}
            placeholder="Add a description..."
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>

        {/* ── Attachment ── */}
        <Form.Item label="Attachment (Optional)">
          {/* Show existing file with remove icon */}
          {showExistingFile && (
            <ExistingFile>
              <FileName>
                <PaperClipOutlined />
                {existingFile.name}
              </FileName>
              <RemoveBtn
                type="button"
                title="Remove attachment"
                onClick={() => setExistingRemoved(true)}
              >
                <DeleteOutlined />
              </RemoveBtn>
            </ExistingFile>
          )}

          {/* Show upload button only when no existing file (or it was removed) */}
          {showUploadBtn && (
            <Form.Item
              name="attachment"
              valuePropName="fileList"
              getValueFromEvent={(e) => e?.fileList}
              noStyle
            >
              <Upload
                beforeUpload={() => false}
                maxCount={1}
                className="custom-upload"
              >
                {!newFileSelected && (
                  <div className="upload-box">
                    <UploadOutlined />
                    <span style={{ marginLeft: 8 }}>Upload File</span>
                  </div>
                )}
              </Upload>
            </Form.Item>
          )}
        </Form.Item>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <ButtonOutlined onClick={handleClose}>Cancel</ButtonOutlined>
          <ButtonFilled type="primary" htmlType="submit" loading={loading}>
            {isEdit ? "Update" : "Add"}
          </ButtonFilled>
        </div>
      </Form>
    </ModalWrap>
  );
};

export default AddAssetModal;
